import { Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ColDef,
  ColGroupDef,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  EXPORT_SCHEDULE_CONTEXT_OBJECT_CLASSES,
  NomObjectLifecycleStatus,
} from 'src/app/core/constants';
import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgBooleanCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgDateRangeFilterComponent } from 'src/app/ag-grid/components/ag-date-range-filter/ag-date-range-filter.component';
import {
  buildAuditTrailColumnGroup,
  buildOwnerColumnDef,
} from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { ObjectEditorService } from 'src/app/core/services/object-editor.service';
import { ToastService } from '@services/toast.service';
import { redirectToObject } from 'src/app/shared/utils/object-utils';
import { DataGridHostBase } from 'src/app/ag-grid/components/data-grid/data-grid-host-base';

/** `my` = the user's own My Schedules (Analytics); `admin` = Client Admin, every company schedule. */
export type ExportSchedulesScope = 'my' | 'admin';

/**
 * Export Schedules grid in two contexts decided by the scope (Rules grid pattern:
 * one host, two routes). Scope comes from the `scope` input first, then route
 * data (`schedulesScope`). The scope decides the view id (each keeps its own
 * presets), the title, the data call and the audit-trail area; the column set
 * is one list.
 */
@Component({
  selector: 'app-export-schedules-grid',
  templateUrl: './export-schedules-grid.component.html',
  standalone: false,
})
export class ExportSchedulesGridComponent
  extends DataGridHostBase
  implements OnInit
{
  private readonly userApi = inject(UserApiService);
  private readonly companyService = inject(CompanyService);
  private readonly editService = inject(ObjectEditorService);
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** Route-free mounts set it; routed ones carry it in route data. */
  @Input() scope?: ExportSchedulesScope;

  private admin = false;

  viewId: ViewId = ViewId.ExportSchedules;
  title = '';
  readonly icon: IconProp = ['fas', 'calendar-alt'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.admin
      ? this.companyService.fetchExportSchedulesAgGridModel(request, 'table')
      : this.userApi.getUserSchedulesAgGrid(request);

  /** Built in ngOnInit: the audit-trail area depends on the scope. */
  columnDefs: Array<ColDef | ColGroupDef> = [];

  ngOnInit(): void {
    const scope: ExportSchedulesScope =
      this.scope ?? this.route.snapshot.data['schedulesScope'] ?? 'my';
    this.admin = scope === 'admin';
    this.viewId = this.admin
      ? ViewId.AdminExportSchedules
      : ViewId.ExportSchedules;
    this.title = this.translateService.instant(
      this.admin
        ? 'export-schedules-grid.title.export-schedules'
        : 'export-schedules.title.my-schedules',
    );
    this.columnDefs = this.buildColumnDefs(this.admin ? 'admin' : 'analytics');
  }

  private buildColumnDefs(
    area: 'admin' | 'analytics',
  ): Array<ColDef | ColGroupDef> {
    return [
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-company',
        ),
        initialHide: true,
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'company.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            initialHide: true,
          },
          {
            headerName: this.translateService.instant('grid.column.is-enabled'),
            field: 'company.is_enabled',
            minWidth: 100,
            maxWidth: 120,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.company.is_enabled',
            },
            initialHide: true,
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-parent',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.workspace'),
            field: 'workspace.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 200,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'workspace',
              icon: ['fas', 'folder'],
              // The workspace's own can_view gates the link (renderer); only
              // refs the dispatcher can open are links.
              clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
              onClick: (ref) => this.editService.openDataObjectRef(ref),
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'grid.column.schedule-context-object-class',
                fieldKey: 'context_object.object_class_id',
                fieldValue: 'context_object.class_title',
                nomenclatureName: 'object_class',
                includeIds: EXPORT_SCHEDULE_CONTEXT_OBJECT_CLASSES,
                otherFilterParams: {
                  defaultToNothingSelected: true,
                },
              },
            ),
            minWidth: 110,
            maxWidth: 150,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.schedule-context-object-title',
            ),
            field: 'context_object.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 240,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'context_object',
              // Only refs with a UI target (URL or settings dialog) are links.
              clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
              onClick: (ref) => this.editService.openDataObjectRef(ref),
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'grid.column.lifecycle-status',
                fieldKey: 'context_object.lifecycle_status',
                fieldValue: 'context_object.lifecycle_status_name',
                nomenclatureName: 'object_lifecycle_status',
                otherFilterParams: {
                  defaultToNothingSelected: true,
                },
              },
            ),
            minWidth: 110,
            maxWidth: 150,
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-details',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'title',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'title',
              objectId: 'id',
              objectClassId: 'object_class_id',
              lifecycleStatus: 'lifecycle_status.id',
              onClick: (data) => {
                if (
                  data?.lifecycle_status.id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  redirectToObject(data, this.router, true);
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'export-schedules.error.you-do-not-have-permissions-or-schedule-not-active',
                    ),
                    this.translateService.instant(
                      'export-schedules.error.not-available',
                    ),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'grid.column.description',
            ),
            field: 'description',
            filter: 'agTextColumnFilter',
            initialHide: true,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.schedule-time-of-day',
            ),
            field: 'schedule_time_of_day_to_send',
            filter: 'agTextColumnFilter',
          },
          {
            headerName: this.translateService.instant(
              'grid.column.recipients-count',
            ),
            field: 'recipients_count',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            sortable: true,
            maxWidth: 160,
          },
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'grid.column.schedule-execution-status',
                fieldKey: 'execution_status.id',
                fieldValue: 'execution_status.title',
                nomenclatureName: 'export_schedule_execution_status',
                otherFilterParams: {
                  defaultToNothingSelected: true,
                },
              },
            ),
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-last-triggered',
            ),
            field: 'time_last_trigger.absolute',
            filter: AgDateRangeFilterComponent,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-last-sent',
            ),
            field: 'time_last_sent.absolute',
            filter: AgDateRangeFilterComponent,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-next-trigger',
            ),
            field: 'time_next_trigger.absolute',
            filter: AgDateRangeFilterComponent,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'grid.column.lifecycle-status',
                fieldKey: 'lifecycle_status.id',
                fieldValue: 'lifecycle_status.title',
                nomenclatureName: 'object_lifecycle_status',
                otherFilterParams: {
                  defaultToNothingSelected: true,
                },
              },
            ),
          },
        ],
      },
      buildOwnerColumnDef(this.translateService, this.openUser),
      buildAuditTrailColumnGroup(this.translateService, {
        area,
        onUserClick: this.openUser,
      }),
    ];
  }
}
